import { useMemo } from "react";
import { useAtomValue } from "jotai";

import {
  buildDashboardCards,
  DashboardInputsType,
} from "@/domain/dashboardCards";
import { useIsDevToolsEnabled } from "@/hooks/useIsDevToolsEnabled";
import {
  dashboardScenarioAtom,
  dismissedDashboardCardIdsAtom,
  submissionConfirmationApplicationIdsAtom,
} from "@/stores/atoms";

import {
  useApplicationsCountQuery,
  useListApplicationsQuery,
} from "./applicationsQueries";
import { useGetHouseholdQuery } from "./householdsQueries";
import { useListRedeterminationsQuery } from "./redeterminationsQueries";
import { useListWorkRequirementsQuery } from "./workRequirementsQueries";

/**
 * Composes the dashboard's server-state dimensions into the derived
 * "Next steps" card feed plus the resolved inputs the persistent sections read.
 *
 * In non-production environments a DEV scenario override (set by the scenario
 * panel) replaces the live query results, so the entire dashboard reflects the
 * chosen scenario. Live dismissals still apply on top of an override.
 */
export const useDashboardCards = () => {
  const { data: household } = useGetHouseholdQuery();

  const { data: submittedApplications } = useListApplicationsQuery({
    filters: { status: "SUBMITTED" },
  });

  const { data: medicaidDraftCount } = useApplicationsCountQuery({
    applicationType: "MEDICAID",
    status: "DRAFT",
  });
  const { data: medicareDraftCount } = useApplicationsCountQuery({
    applicationType: "MEDICARE",
    status: "DRAFT",
  });
  const { data: marketplaceDraftCount } = useApplicationsCountQuery({
    applicationType: "MARKETPLACE",
    status: "DRAFT",
  });

  // Fetch every status: NOT_STARTED/PENDING_FORM/CALL_BACK drive Next-steps
  // cards, while PENDING_FORM/CALL_BACK/COMPLETED populate the Renewals catalog.
  const { data: redeterminations } = useListRedeterminationsQuery({
    filters: {
      status: ["NOT_STARTED", "PENDING_FORM", "CALL_BACK", "COMPLETED"],
    },
  });

  const { data: workRequirements } = useListWorkRequirementsQuery();

  const dismissedCardIds = useAtomValue(dismissedDashboardCardIdsAtom);
  const submissionConfirmationApplicationIds = useAtomValue(
    submissionConfirmationApplicationIdsAtom,
  );
  const scenario = useAtomValue(dashboardScenarioAtom);

  const realInputs: DashboardInputsType = useMemo(
    () => ({
      household,
      submittedApplications,
      draftCounts: {
        medicaid: medicaidDraftCount ?? 0,
        medicare: medicareDraftCount ?? 0,
        marketplace: marketplaceDraftCount ?? 0,
      },
      redeterminations,
      workRequirements,
      dismissedCardIds,
      submissionConfirmationApplicationIds,
    }),
    [
      household,
      submittedApplications,
      medicaidDraftCount,
      medicareDraftCount,
      marketplaceDraftCount,
      redeterminations,
      workRequirements,
      dismissedCardIds,
      submissionConfirmationApplicationIds,
    ],
  );

  const isDevToolsEnabled = useIsDevToolsEnabled();
  const isScenarioActive = isDevToolsEnabled && scenario !== null;

  const inputs: DashboardInputsType = useMemo(
    () =>
      isScenarioActive && scenario
        ? { ...scenario, dismissedCardIds }
        : realInputs,
    [isScenarioActive, scenario, dismissedCardIds, realInputs],
  );

  const cards = buildDashboardCards(inputs);

  return { cards, inputs, isScenarioActive };
};
